import { createTheme } from '@mui/material';

// American flag colors
const PRIMARY_GRAY = '#333333';
const OLD_GLORY_RED = '#BF0D3E';

const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',
      light: '#222222',
      dark: '#111111',
      contrastText: '#ffffff',
    },
    secondary: {
      main: OLD_GLORY_RED,
      light: '#d4325c',
      dark: '#8f0a2f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F5F7FA',
      paper: '#ffffff',
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    info: {
      main: '#0288D1',
      light: '#03A9F4',
      dark: '#01579B',
    },
    text: {
      primary: '#2A2A2A',
      secondary: '#5A5A5A',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      marginBottom: '0.5rem',
    },
    h4: {
      fontWeight: 600,
      marginBottom: '0.5rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent)',
            transform: 'translateX(-100%)',
          },
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
            '&::before': {
              transform: 'translateX(100%)',
              transition: 'transform 0.75s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          },
          '&:active': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          backgroundImage: `linear-gradient(135deg, ${PRIMARY_GRAY} 0%, #021224 100%)`,
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.1)',
        },
        containedSecondary: {
          backgroundImage: `linear-gradient(135deg, ${OLD_GLORY_RED} 0%, #8f0a2f 100%)`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.01)',
            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: `linear-gradient(135deg,rgb(21, 21, 21) 0%,rgb(41, 40, 40) 100%)`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '30%',
            height: '100%',
            background: 'linear-gradient(115deg, transparent, rgba(255,255,255,0.1))',
            opacity: 0.6
          }
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#021224',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          '& .MuiList-root': {
            padding: 0
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            }
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          WebkitBackgroundClip: 'text',
          textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
        },
        h2: {
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '3px',
            backgroundImage: `linear-gradient(90deg, #222222, ${OLD_GLORY_RED})`,
            borderRadius: '2px',
          },
        },
      },
    },
  },
});

export default theme; 